

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewTravelOrderItemPaxModal = ({
    isOpen,
    onClose,
    onSave,
    orderItemId = null,
    orderPaxId = null
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        order_item_id: orderItemId || '',
        order_pax_id: orderPaxId || ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [orderItems, setOrderItems] = useState([]);
    const [orderPax, setOrderPax] = useState([]);
    const [loadingOrderItems, setLoadingOrderItems] = useState(false);
    const [loadingOrderPax, setLoadingOrderPax] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            if (!orderItemId) {
                fetchOrderItems();
            }
            if (!orderPaxId) {
                fetchOrderPax();
            }
        }
    }, [isOpen, orderItemId, orderPaxId]);

    const fetchOrderItems = async () => {
        setLoadingOrderItems(true);
        try {
            const response = await axiosInstance.get('/travel-order-items/');
            setOrderItems(response.data || []);
        } catch (error) {
            console.error('Error fetching travel order items:', error);
        } finally {
            setLoadingOrderItems(false);
        }
    };

    const fetchOrderPax = async () => {
        setLoadingOrderPax(true);
        try {
            const response = await axiosInstance.get('/travel-order-pax/');
            setOrderPax(response.data || []);
        } catch (error) {
            console.error('Error fetching travel order pax:', error);
        } finally {
            setLoadingOrderPax(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.order_item_id) errors.order_item_id = 'Order Item is required';
        if (!formData.order_pax_id) errors.order_pax_id = 'Order Pax is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                order_item_id: parseInt(formData.order_item_id),
                order_pax_id: parseInt(formData.order_pax_id),
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                name: '',
                order_item_id: orderItemId || '',
                order_pax_id: orderPaxId || ''
            });
        } catch (error) {
            console.error('Error saving Travel Order Item Pax:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Travel Order Item Passenger',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '600px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Travel Order Item Passenger</MDTypography>
                        <IconButton onClick={onClose} variant="outlined" margin="normal" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined" margin="normal"
                                    />
                                </Grid>

                                {!orderItemId && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.order_item_id} required>
                                            <InputLabel>Order Item</InputLabel>
                                            <Select
                                                name="order_item_id"
                                                value={formData.order_item_id}
                                                onChange={handleInputChange}
                                                label="Order Item"

                                                disabled={loadingOrderItems}
                                            >
                                                <MenuItem value=""><em>Select an Order Item</em></MenuItem>
                                                {orderItems.map(oi => (
                                                    <MenuItem key={oi.id} value={oi.id}>{oi.name}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.order_item_id && <MDTypography variant="caption" color="error">{formErrors.order_item_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}

                                {!orderPaxId && (
                                    <Grid item xs={12}>
                                        <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.order_pax_id} required>
                                            <InputLabel>Order Pax</InputLabel>
                                            <Select
                                                name="order_pax_id"
                                                value={formData.order_pax_id}
                                                onChange={handleInputChange}
                                                label="Order Pax"

                                                disabled={loadingOrderPax}
                                            >
                                                <MenuItem value=""><em>Select an Order Passenger</em></MenuItem>
                                                {orderPax.map(op => (
                                                    <MenuItem key={op.id} value={op.id}>{op.passenger_name}</MenuItem>
                                                ))}
                                            </Select>
                                            {formErrors.order_pax_id && <MDTypography variant="caption" color="error">{formErrors.order_pax_id}</MDTypography>}
                                        </FormControl>
                                    </Grid>
                                )}
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Item Passenger'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewTravelOrderItemPaxModal;

